import { AlertCircle, Edit, Download, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';

const getPaymentFailureDetail = (payment) => {
  const details = [
    payment.payment_response_message,
    payment.payment_failure_reason ? `Código PagBank: ${payment.payment_failure_reason}` : null,
    payment.payment_response_code ? `Resposta: ${payment.payment_response_code}` : null,
    payment.pagseguro_charge_id ? `Charge: ${payment.pagseguro_charge_id}` : null,
  ].filter(Boolean);

  return details.join(' | ');
};

export default function RegistrationsTab({
  events,
  registrations,
  selectedEvent,
  setSelectedEvent,
  onEditRegistration,
  onResendEmail,
  onUpdatePaymentStatus,
  onExportCSV,
}) {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-heading font-bold uppercase text-primary">Gerenciar Inscrições</h2>
        <div className="flex gap-2">
          <select
            data-testid="filter-registrations-event"
            value={selectedEvent}
            onChange={(e) => setSelectedEvent(e.target.value)}
            className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
          >
            <option value="all">Todos os Eventos</option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>{event.name}</option>
            ))}
          </select>
          <Button
            data-testid="export-csv-button"
            onClick={onExportCSV}
            className="bg-accent text-accent-foreground hover:bg-accent/90 uppercase tracking-widest font-bold px-6 py-3 rounded-sm"
          >
            <Download className="w-5 h-5 mr-2" />
            Exportar CSV
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full">
          <thead className="bg-secondary">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-widest text-primary">Participante</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-widest text-primary">Email</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-widest text-primary">Telefone</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-widest text-primary">Evento</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-widest text-primary">Categoria</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-widest text-primary">Valor</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-widest text-primary">Status</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-widest text-primary">Data</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-widest text-primary">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {registrations
              .filter(reg => selectedEvent === 'all' || reg.event_id === selectedEvent)
              .map((registration) => {
                const event = events.find(e => e.id === registration.event_id);
                const paymentFailureDetail = getPaymentFailureDetail(registration);
                return (
                  <tr key={registration.id} data-testid={`registration-${registration.id}`}>
                    <td className="px-4 py-3 font-bold text-foreground">{registration.nome} {registration.sobrenome}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{registration.email}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{registration.telefone}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{event?.name || 'N/A'}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs uppercase tracking-widest font-bold text-primary bg-secondary px-3 py-1 rounded-sm">
                        {registration.categoria}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-bold text-accent">R$ {registration.amount?.toFixed(2) || '0.00'}</td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span
                            title={paymentFailureDetail || undefined}
                            className={`text-xs uppercase tracking-widest font-bold px-3 py-1 rounded-sm ${
                              registration.payment_status === 'paid'
                                ? 'bg-green-100 text-green-800'
                                : registration.payment_status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {registration.payment_status === 'paid' ? 'Pago' : registration.payment_status === 'pending' ? 'Pendente' : 'Falhou'}
                          </span>
                          {registration.payment_status === 'failed' && paymentFailureDetail && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => alert(paymentFailureDetail)}
                              className="h-7 w-7 p-0 rounded-sm text-red-700"
                              title="Ver motivo da falha"
                            >
                              <AlertCircle className="w-4 h-4" />
                            </Button>
                          )}
                          {registration.payment_status !== 'paid' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => onUpdatePaymentStatus(registration.id, 'paid')}
                              className="h-7 text-xs rounded-sm"
                              title="Marcar como pago (pagamento presencial)"
                            >
                              Pagar
                            </Button>
                          )}
                        </div>
                        {registration.payment_status === 'failed' && paymentFailureDetail && (
                          <p className="max-w-xs truncate text-xs text-red-700" title={paymentFailureDetail}>
                            {paymentFailureDetail}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {new Date(registration.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button
                          data-testid={`edit-registration-${registration.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => onEditRegistration(registration)}
                          className="rounded-sm"
                          title="Editar inscrição"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`resend-email-${registration.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => onResendEmail(registration.id)}
                          className="rounded-sm"
                          title="Reenviar email com dados do boleto"
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {registrations.filter(reg => selectedEvent === 'all' || reg.event_id === selectedEvent).length === 0 && (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <p className="text-foreground/50">Nenhuma inscrição encontrada.</p>
        </div>
      )}
    </div>
  );
}
